import { CountdownButton } from '@/components/common/count-down-button';
import { HomeButton } from '@/components/common/home-button';
import { HelpPopup, ScreenSaverListPopup } from '@/components/feature/home';
import { HomePageSwiper } from '@/components/feature/home/home-page-slider';
import { BellIcon } from '@/components/icons';
import { PaperIcon } from '@/components/icons/PaperIcon';
import { useAccessibilityTTS, useRestoreFocusOnClose } from '@/hooks';
import { TrackingService } from '@/services/analytics/TrackingService';
import { useConfigStore } from '@/stores/configStore';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function HomePage() {
    const { t } = useTranslation();
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
    const [visibleScreenSaverList, setVisibleScreenSaverList] = useState(false);
    const { config } = useConfigStore();
    const tts = useConfigStore((state) => state.config?.tts);
    const updateConfig = useConfigStore((state) => state.updateConfig);
    const ui = useConfigStore((state) => state.config?.ui);

    const { restoreFocus } = useRestoreFocusOnClose({ active: isHelpModalOpen });
    const { speak } = useAccessibilityTTS();

    const screenSaver = config?.ui?.screenSaver || [];

    // When headphone is connected, hide countdown button
    const shouldHideCountdownButton = () => {
        return tts?.voice === true && tts?.speaker === 'headphone';
    };

    useEffect(() => {
        if (tts?.speaker !== 'headphone') {
            updateConfig({
                ui: {
                    ...ui,
                    zoomIn: false,
                    zoomClosed: true,
                    contrast: false,
                    volume: 3,
                    screenMode: 'fullscreen',
                    zoomLevel: 1,
                },
                tts: {
                    ...tts,
                    voice: false,
                },
            });
        }
    }, [])

    const handleHelpRequest = () => {
        setIsHelpModalOpen(true);
        TrackingService.onHomePopupOpen();
    };

    const handleCloseHelpModal = () => {
        restoreFocus();
        setIsHelpModalOpen(false);
        TrackingService.onHomePopupClose();
    };

    return (
        <main className="fullscreen:h-full widescreen:h-full relative h-[calc(1450*var(--vw-unit))] w-full">
            <HomePageSwiper screenSaver={screenSaver} />
            <section className="absolute bottom-[calc(20*var(--vh-unit))] left-[calc(24*var(--vw-unit))] flex gap-[calc(12*var(--vh-unit))]">
                <HomeButton
                    role="button"
                    data-tts-scope="nearby-poi-list-button"
                    data-role={t('common.button.tts.popup_button')}
                    prefix={<PaperIcon />}
                    data-interactive
                    data-playsound="ting"
                    onClick={() => {
                        speak(t('home.button.screen_saver_list') + ' ' + t('common.button.tts.popup_button'));
                        setVisibleScreenSaverList(true);
                        TrackingService.onHomePopupOpen();
                    }}
                    onFocus={() => {
                        speak(t('home.button.screen_saver_list') + ' ' + t('common.button.tts.popup_button') + t('home.title.screen_saver_list_voice'));
                    }}
                >
                    {t('home.button.screen_saver_list')}
                </HomeButton>
                <HomeButton
                    aria-label={t('employeeHelp.title')}
                    data-role={t('common.button.tts.popup_button')}
                    prefix={<BellIcon />}
                    onClick={handleHelpRequest}
                    data-playsound="ting"
                    data-interactive
                >
                    {t('employeeHelp.title')}
                </HomeButton>
            </section>

            <HelpPopup isOpen={isHelpModalOpen} onClose={handleCloseHelpModal} />
            <ScreenSaverListPopup
                screenSaver={screenSaver}
                isOpen={visibleScreenSaverList}
                onClose={() => {
                    setVisibleScreenSaverList(false);
                    TrackingService.onHomePopupClose();
                }}
            />
            {(isHelpModalOpen || visibleScreenSaverList) && !shouldHideCountdownButton() && (
                <div style={{ display: 'none' }}>
                    <CountdownButton
                        closeModal={() => {
                            setIsHelpModalOpen(false);
                            setVisibleScreenSaverList(false);
                        }}
                    />
                </div>
            )}
        </main>
    );
}
